import type { DeviceModel, DeviceModelLookupResponse } from '@techgitter/contracts';

const API_BASE_URL = '/api';

/**
 * Resolve a captured model number (A-number, Surface "Model 1866", MTM …) to
 * the device(s) it names — `GET /api/device-models` in sm4rt-api, over
 * `cr001_device_models`.
 *
 * Every match comes back, never a guess: one number can name several models
 * (A2442 is the 14-inch MacBook Pro with M1 Pro *and* M1 Max). An empty list
 * means the number is unknown, which is not an error. The server normalises
 * the number the same way the inspection does on save, so pass it as stored.
 */
export async function lookupDeviceModels(
  modelNumber: string,
  manufacturer?: string | null,
  signal?: AbortSignal,
): Promise<DeviceModel[]> {
  const qs = new URLSearchParams({ modelNumber });
  if (manufacturer?.trim()) qs.set('manufacturer', manufacturer.trim());

  const response = await fetch(`${API_BASE_URL}/device-models?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to look up model ${modelNumber}: ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as Partial<DeviceModelLookupResponse>;
  return data.items ?? [];
}
